import type { PropsWithChildren } from "react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { atom, useAtomValue, useSetAtom } from "jotai";
import { useHydrateAtoms } from "jotai/utils";

type DtfSideTrackingValue = {
  markSideAsActive: (side: string, processTimeStamp: string) => void;
  markSideAsDeleted: (side: string) => void;
  isSideActive: (side: string, processTimeStamp: string) => boolean;
};

const dtfSideTrackingAtom = atom<DtfSideTrackingValue | null>(null);

function useDtfSideTrackingCreator(): DtfSideTrackingValue {
  const activeSides = useRef<Record<string, string>>({});

  const markSideAsActive = useCallback((side: string, processTimeStamp: string) => {
    activeSides.current[side] = processTimeStamp;
  }, []);

  const markSideAsDeleted = useCallback((side: string) => {
    delete activeSides.current[side];
  }, []);

  const isSideActive = useCallback(
    (side: string, processTimeStamp: string) => activeSides.current[side] === processTimeStamp,
    []
  );

  return useMemo(
    () => ({ markSideAsActive, markSideAsDeleted, isSideActive }),
    [markSideAsActive, markSideAsDeleted, isSideActive]
  );
}

/**
 * Provider that creates and shares a single side tracking instance across all consumers.
 * Must be placed high in the component tree (e.g., in DesignLabRoute).
 */
export function DtfSideTrackingProvider({ children }: PropsWithChildren) {
  const value = useDtfSideTrackingCreator();

  // Initial hydration (runs once synchronously during first render)
  useHydrateAtoms([[dtfSideTrackingAtom, value]]);

  // Sync updates when provider remounts (e.g., error boundaries, route transitions, StrictMode)
  const setSideTracking = useSetAtom(dtfSideTrackingAtom);
  useEffect(() => {
    setSideTracking(value);
  }, [value, setSideTracking]);

  return children;
}

/**
 * Provides tracking functions for marking and checking active sides by process timestamp.
 *
 * Uses a shared ref via Jotai atom for immediate visibility of mutations within async operations
 * across all components.
 *
 * @returns An object with tracking functions:
 *  - `markSideAsActive(side, processTimeStamp)` — records the given `processTimeStamp` for `side`.
 *  - `markSideAsDeleted(side)` — removes tracking for `side`.
 *  - `isSideActive(side, processTimeStamp)` — `true` if the recorded timestamp for `side` equals `processTimeStamp`, `false` otherwise.
 */
export function useDtfSideTracking(): DtfSideTrackingValue {
  const value = useAtomValue(dtfSideTrackingAtom);

  if (!value) {
    throw new Error("useDtfSideTracking must be used within DtfSideTrackingProvider");
  }

  return value;
}
